<!--
  Contract Report.

  The fifth quality card: a job billed on equipment days and operator days
  instead of m³ of concrete. Copied from DailyReportForm.svelte and adapted —
  same raw-string state, explicit Save, add/remove/reorder rows, delete
  behind an in-page confirmation banner rather than confirm(). Unlike a Daily
  Report this record has no `project`/`weather` columns of its own (see
  quality-contract-reports.ts): the site link is what ties it to a job.

  The card itself carries a RANGE — `report_date`/`end_date` — not just one
  day, so a multi-day hire or a job spanning a week is one card, not one per
  day.

  Four independent child tables in printed order — equipment, hired in,
  operators, activities — then notes and prepared-by/reviewed-by. Equipment
  and operators use the SAME pick-list-with-free-text idiom DailyReportForm
  uses twice already (OTHER_* sentinel, custom* boolean array parallel to the
  rows, *Choices derived list, *Value/choose* pair, add/remove/move helpers
  that carry the flag with the row) — against api.listEquipmentOptions() and
  api.listPersonnelOptions() respectively, so a machine or a new hire not yet
  set up is still recordable from the field. Hired in has NO pick list: a
  rented machine or a subcontractor's crew is typed, not chosen off SCI's own
  roster, so it is a plain name field plus a two-option kind select — simpler
  than the other two, deliberately, rather than a fourth pick-list idiom
  invented for a shape that does not need one.

  Equipment and hired-in rows bill on DATES, not a typed quantity: each row
  carries its own `start_date`/`end_date`, blank meaning "use the card's own
  range" (see calc/contract.ts), with the derived day count — `daysInclusive`,
  this app's one day-count rule, applied here exactly as calc/contract.ts
  applies it when pricing, so the number shown can never disagree with the
  number that bills — rendered beside them as read-only text. Whole days
  only, inclusive at both ends: the 1st to the 20th is 20 days. `days` is
  retired on the wire (see SaveContractReportRequest) but a value is still
  sent, computed rather than typed, because the column has not left the
  database. An operator still carries a typed `days` (half days are real) and
  `time_in`/`time_out` — the paper record and the input to labour cost — an
  operator has no date range of its own to bill against.

  **Equipment and hire rows are id-stable on the server (Task 4), so THIS
  FORM MUST SEND EVERY EXISTING ROW'S id BACK.** A row the user just added
  has none, which is correct and means "new"; add/remove/move never touch an
  id once loaded. Dropping the id off a row that already has one reads as
  "this row is gone" — the server now refuses a save that would silently
  discard a dated equipment row or a priced hire rather than guess at what
  the client meant.

  Hired in records what SCI brought ONTO the job — a rented machine or a
  subcontractor's crew — separate from SCI's own equipment above. Its
  `cost_per_day` is what SCI PAYS, never billed, and is admin-only: the input
  is gated `{#if $isAdmin}` with NO read-only fallback for a quality session,
  UNLIKE `bills_concrete`'s precedent in SiteFields.svelte, which this form
  otherwise follows for the gate itself — a flag is safe to show read-only,
  money reaching a quality screen is not. The route already strips the field
  from a non-admin session's response, so there is nothing honest to show
  read-only even if this form tried.

  Every child list starts EMPTY on a new report, on both ends: a contract day
  has no documented default equipment, hire or crew the way a Daily Report's
  deliveries do.

  The activities table is copied unchanged from DailyReportForm, mix design
  pick-list included: a pour is recorded the same way wherever it happens.

  Cancelled + its note sit above the four child tables — the day still gets a
  ticket number, but equipment and hires still on site still bill; only
  operators do not (calc/contract.ts's own rule: billing their days on top of
  the cancellation fee would charge twice for the same non-work). So the
  Operators card is not offered at all while Cancelled is ticked, with a line
  saying why, rather than simply vanishing.

  No acceptance verdicts, and — the standing invariant for every quality
  screen — no dollar figure anywhere: a rate, a total, a computed amount,
  with hired-in's admin-gated `cost_per_day` the one deliberate exception
  above. The crew records facts; admin prices them (Task 7's Rates tabs, not
  here). The print button is the same invariant on paper:
  ../../lib/export/quality-pdf.ts's buildContractReportPrintBody carries no
  money either, and print() below — copied from DailyReportForm's own —
  prints what is on screen, not what was last saved.
-->
<script lang="ts">
  import { api, isApiError } from '../../lib/api';
  import SitePicker from './SitePicker.svelte';
  import { createAutosave } from '../../lib/autosave';
  import { claimAddress, linkHandler, location as appLocation, navigate, routePath, siteIdFromQuery } from '../../lib/router';
  import { advanceOnEnter } from '../../lib/fields/advance-on-enter';
  import { resolveRowRange } from '../../lib/calc/contract';
  import { foldName, totalCubes } from '../../lib/calc/pnl';
  import { cubes } from '../../lib/money';
  import { quickCubes, quickTime } from '../../lib/calc/quick-entry';
  import { printContractReport } from '../../lib/export/quality-pdf';
  import { isAdmin, notify, notifyError, toInput, toNumber } from '../../lib/store';
  import type {
    DailyReportActivityInput,
    MixDesignOption,
    RateUnit,
    SaveContractReportRequest,
    SiteSummary
  } from '../../lib/types';

  interface Props {
    /** null for a new report. */
    contractReportId?: number | null;
  }

  let { contractReportId = null }: Props = $props();

  let loading = $state(contractReportId !== null);
  let saving = $state(false);
  let deleting = $state(false);
  let error = $state<string | null>(null);
  let fieldErrors = $state<Record<string, string>>({});
  /** The id once saved; a new report gets one on its first save. */
  let savedId = $state<number | null>(contractReportId);

  let siteId = $state<number | null>(null);
  let reportDate = $state('');
  /** Blank means the card is one day (`reportDate` alone) — see calc/contract.ts. */
  let endDate = $state('');
  let ticketNo = $state('');
  let notes = $state('');
  let preparedBy = $state('');
  let reviewedBy = $state('');
  let cancelled = $state(false);
  let cancellationNote = $state('');

  /**
   * `id` is `null` for a row the user just added — that IS "new" — and is
   * never touched again once a row is loaded with one. THIS FORM MUST SEND
   * IT BACK on save: the server reconciles equipment/hire rows by id, not
   * by wholesale replace, and a row an existing id was dropped from is read
   * as deleted (see this file's header comment and quality-contract-reports.ts).
   *
   * `start_date`/`end_date` are kept as plain strings — blank means "use the
   * card's own range" (calc/contract.ts), a real and different value from
   * "unset", which is why every row always sends both keys rather than
   * omitting a blank one.
   */
  interface EquipmentRow {
    id: number | null;
    equipment: string;
    start_date: string;
    end_date: string;
    /**
     * Which of the machine's rates this row bills on. The crew picks it; this
     * screen never sees the rate itself, only the unit. The day count still
     * comes from the dates — the unit only chooses day vs week vs month.
     */
    rate_unit: RateUnit;
  }
  interface OperatorRow {
    name: string;
    days: string;
    time_in: string;
    time_out: string;
  }
  /**
   * What SCI hired in — a rented machine or a subcontractor's crew, on its
   * own date range. Same id-back requirement as EquipmentRow, above.
   *
   * `cost_per_day` is kept as text like every other numeric field here, but
   * ONLY has a rendered input for an admin session (`$isAdmin`) — see the
   * Hired in card below and this file's header comment. A quality session's
   * row simply has no figure typed into it, ever; it stays '' and is never
   * sent (see `body()`), the same way the route never sends one back on load.
   */
  interface HireRow {
    id: number | null;
    name: string;
    kind: 'equipment' | 'subcontractor';
    start_date: string;
    end_date: string;
    cost_per_day: string;
  }

  // Every child list starts EMPTY, on a new report as much as an existing
  // one — a contract day has no documented default equipment, hire or crew.
  let equipment = $state<EquipmentRow[]>([]);
  let operators = $state<OperatorRow[]>([]);
  let activities = $state<DailyReportActivityInput[]>([]);
  let hires = $state<HireRow[]>([]);

  /** The mix design library as a pick-list; loaded once, a convenience only. */
  let mixOptions = $state<MixDesignOption[]>([]);

  /** Equipment that carries a charge rate, names only — this screen is never
   *  told what anything costs to run or bills per day. */
  let equipmentOptions = $state<string[]>([]);

  /** The active roster, for the operator pick-list. Names only — this screen
   *  is never told what anyone is paid or billed out at. */
  let rosterNames = $state<string[]>([]);

  $effect(() => {
    api
      .listMixDesignOptions()
      .then((response) => {
        mixOptions = response.mix_designs;
      })
      .catch(() => {
        // The typed code still saves; only the pick-list is missing.
      });
  });

  $effect(() => {
    api
      .listEquipmentOptions()
      .then((response) => {
        equipmentOptions = response.equipment;
      })
      .catch(() => {
        // "Something else…" still takes any name, so a machine nobody has set
        // up yet is never blocked from being recorded.
      });
  });

  $effect(() => {
    api
      .listPersonnelOptions()
      .then((response) => {
        rosterNames = response.names;
      })
      .catch(() => {
        // Same bargain: "Someone else…" still takes any name, so a field
        // crew is never blocked from recording who worked because an admin
        // list would not load.
      });
  });

  $effect(() => {
    if (contractReportId === null) return;
    loading = true;
    api
      .getContractReport(contractReportId)
      .then((response) => {
        const report = response.contract_report;
        siteId = report.site_id;
        reportDate = report.report_date;
        endDate = report.end_date ?? '';
        ticketNo = report.ticket_no;
        notes = report.notes;
        preparedBy = report.prepared_by;
        reviewedBy = report.reviewed_by;
        cancelled = report.cancelled === 1;
        cancellationNote = report.cancellation_note;
        // id carried straight through — see EquipmentRow's own comment.
        equipment = response.equipment.map((row) => ({
          id: row.id,
          equipment: row.equipment,
          start_date: row.start_date ?? '',
          end_date: row.end_date ?? '',
          // Absent on a row written before the unit existed — read as 'day',
          // the same default the server and the pricing engine use.
          rate_unit: row.rate_unit ?? 'day'
        }));
        // A saved name is a pick-list entry, whatever it says: the loaded
        // rows seed the options, so even one typed before the list existed
        // comes back selected rather than blank.
        customEquipment = [];
        operators = response.operators.map((row) => ({
          name: row.name,
          days: toInput(row.days),
          time_in: row.time_in,
          time_out: row.time_out
        }));
        customOperator = [];
        activities = response.activities.map((row) => ({
          piling_number: row.piling_number,
          unit_number: row.unit_number,
          start_time: row.start_time,
          stop_time: row.stop_time,
          amount_supplied: row.amount_supplied,
          admixtures_used: row.admixtures_used,
          water_used: row.water_used,
          mix_design_id: row.mix_design_id
        }));
        // `cost_per_day` is present only for an admin session (the route
        // strips it for quality — PublicContractReportHire) — 'in' narrows
        // the union per row rather than assuming every row carries it.
        hires = response.hires.map((row) => ({
          id: row.id,
          name: row.name,
          kind: row.kind,
          start_date: row.start_date,
          end_date: row.end_date,
          cost_per_day: 'cost_per_day' in row ? toInput(row.cost_per_day) : ''
        }));
        savedId = report.id;
        // The loaded document is the auto-save baseline: nothing is dirty yet.
        lastSaved = JSON.stringify(body());
      })
      .catch((cause) => {
        notifyError(cause, 'Could not load that contract report');
        error = 'Could not load that contract report.';
      })
      .finally(() => {
        loading = false;
      });
  });

  // --- date ranges ---------------------------------------------------------

  /**
   * A row's own dates, falling back to the card's own range when either is
   * blank — `calc/contract.ts`'s own `resolveRowRange`, the SAME function
   * `rangedRows` calls when it prices this same row (Task 5 review, I3/M1:
   * this used to be a hand-copied three-line version of that fallback,
   * pinned together only by both sides also calling `daysInclusive` — now
   * there is one copy, imported, so the two cannot drift). Null when the
   * resolved range does not parse as real dates, or is reversed — shown as
   * an em dash rather than a wrong number.
   *
   * Shared by equipment and hires: both bill the same way, on their own
   * range or the card's.
   */
  function rowDayCount(rowStart: string, rowEnd: string): number | null {
    return resolveRowRange(rowStart, rowEnd, reportDate, endDate || reportDate).days;
  }

  // --- equipment ---------------------------------------------------------

  /**
   * The value the "Something else…" option carries. Each listed name is its
   * INDEX in `equipmentChoices`, never the name itself, so no name can
   * collide with this sentinel or with the blank option.
   */
  const OTHER_EQUIPMENT = 'other';

  /** Rows showing the free-text box instead of the pick-list, by row index. */
  let customEquipment = $state<boolean[]>([]);

  function equipmentFlags(): boolean[] {
    return Array.from({ length: equipment.length }, (_, i) => customEquipment[i] === true);
  }

  /**
   * The rate-carrying equipment, plus any name already on a row, so a report
   * saved before this existed never blanks a name typed by hand. Folded
   * case-insensitively, first spelling wins.
   *
   * A row being typed into is deliberately left out: its half-finished text
   * has no business appearing in every other row's dropdown.
   */
  const equipmentChoices = $derived.by(() => {
    const onRows = equipment.filter((_, i) => !customEquipment[i]).map((row) => row.equipment);
    const seen = new Set<string>();
    const names: string[] = [];
    for (const name of [...equipmentOptions, ...onRows]) {
      const key = foldName(name);
      if (key === '' || seen.has(key)) continue;
      seen.add(key);
      names.push(name);
    }
    return names;
  });

  function equipmentValue(index: number): string {
    if (customEquipment[index]) return OTHER_EQUIPMENT;
    const key = foldName(equipment[index].equipment);
    if (key === '') return '';
    const at = equipmentChoices.findIndex((name) => foldName(name) === key);
    return at === -1 ? '' : String(at);
  }

  function chooseEquipment(index: number, value: string) {
    const flags = equipmentFlags();
    flags[index] = value === OTHER_EQUIPMENT;
    customEquipment = flags;
    if (value === OTHER_EQUIPMENT) return;
    equipment[index].equipment = value === '' ? '' : (equipmentChoices[Number(value)] ?? '');
  }

  function addEquipment() {
    customEquipment = [...equipmentFlags(), false];
    // id: null — a row the user just added, which the reconcile correctly
    // reads as new. Blank dates default to the card's own range.
    equipment = [...equipment, { id: null, equipment: '', start_date: '', end_date: '', rate_unit: 'day' }];
  }

  function removeEquipment(index: number) {
    customEquipment = equipmentFlags().filter((_, i) => i !== index);
    equipment = equipment.filter((_, i) => i !== index);
  }

  function moveEquipment(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= equipment.length) return;
    const next = [...equipment];
    [next[index], next[target]] = [next[target], next[index]];
    equipment = next;
    const flags = equipmentFlags();
    [flags[index], flags[target]] = [flags[target], flags[index]];
    customEquipment = flags;
  }

  // --- operators -----------------------------------------------------------

  /**
   * The value the "Someone else…" option carries. Each listed name is its
   * INDEX in `operatorChoices`, never the name itself.
   */
  const OTHER_OPERATOR = 'other';

  /** Rows showing the free-text box instead of the pick-list, by row index. */
  let customOperator = $state<boolean[]>([]);

  function operatorFlags(): boolean[] {
    return Array.from({ length: operators.length }, (_, i) => customOperator[i] === true);
  }

  const operatorChoices = $derived.by(() => {
    const onRows = operators.filter((_, i) => !customOperator[i]).map((row) => row.name);
    const seen = new Set<string>();
    const names: string[] = [];
    for (const name of [...rosterNames, ...onRows]) {
      const key = foldName(name);
      if (key === '' || seen.has(key)) continue;
      seen.add(key);
      names.push(name);
    }
    return names;
  });

  function operatorValue(index: number): string {
    if (customOperator[index]) return OTHER_OPERATOR;
    const key = foldName(operators[index].name);
    if (key === '') return '';
    const at = operatorChoices.findIndex((name) => foldName(name) === key);
    return at === -1 ? '' : String(at);
  }

  function chooseOperator(index: number, value: string) {
    const flags = operatorFlags();
    flags[index] = value === OTHER_OPERATOR;
    customOperator = flags;
    if (value === OTHER_OPERATOR) return;
    operators[index].name = value === '' ? '' : (operatorChoices[Number(value)] ?? '');
  }

  function addOperator() {
    customOperator = [...operatorFlags(), false];
    operators = [...operators, { name: '', days: '', time_in: '', time_out: '' }];
  }

  function removeOperator(index: number) {
    customOperator = operatorFlags().filter((_, i) => i !== index);
    operators = operators.filter((_, i) => i !== index);
  }

  function moveOperator(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= operators.length) return;
    const next = [...operators];
    [next[index], next[target]] = [next[target], next[index]];
    operators = next;
    const flags = operatorFlags();
    [flags[index], flags[target]] = [flags[target], flags[index]];
    customOperator = flags;
  }

  // --- hires ---------------------------------------------------------------

  // No pick-list idiom here (unlike equipment/operators) — a hire is typed,
  // not chosen off SCI's own roster. See this file's header comment.

  function addHire() {
    // id: null — new, same rule as addEquipment. cost_per_day starts blank:
    // an admin session can type one; a quality session never sees the field
    // at all (see the Hired in card below), and the server stores a hire
    // with no typed cost as unpriced (0), never a settled-looking figure.
    hires = [...hires, { id: null, name: '', kind: 'equipment', start_date: '', end_date: '', cost_per_day: '' }];
  }

  function removeHire(index: number) {
    hires = hires.filter((_, i) => i !== index);
  }

  function moveHire(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= hires.length) return;
    const next = [...hires];
    [next[index], next[target]] = [next[target], next[index]];
    hires = next;
  }

  // --- activities ------------------------------------------------------------

  function blankActivity(): DailyReportActivityInput {
    return {
      piling_number: '',
      unit_number: '',
      start_time: '',
      stop_time: '',
      amount_supplied: '',
      admixtures_used: '',
      water_used: '',
      mix_design_id: null
    };
  }

  function addActivity() {
    activities = [...activities, blankActivity()];
  }

  function removeActivity(index: number) {
    activities = activities.filter((_, i) => i !== index);
  }

  function moveActivity(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= activities.length) return;
    const next = [...activities];
    [next[index], next[target]] = [next[target], next[index]];
    activities = next;
  }

  /**
   * Choosing a mix sets BOTH fields: the id for the admin area's pricing and
   * the code into the text column, so the printed sheet reads "SC01".
   * Choosing the blank option only clears the id — it leaves water_used
   * alone, so a row typed as e.g. "CC520N" before the pick-list existed
   * keeps that text on the printed sheet.
   */
  function chooseMix(index: number, value: string) {
    if (value === '') {
      activities[index].mix_design_id = null;
      return;
    }
    const option = mixOptions.find((m) => m.id === Number(value)) ?? null;
    activities[index].mix_design_id = option ? option.id : null;
    activities[index].water_used = option ? option.mix_code : '';
  }

  function body(): SaveContractReportRequest {
    return {
      site_id: siteId,
      report_date: reportDate,
      end_date: endDate,
      ticket_no: ticketNo,
      prepared_by: preparedBy,
      reviewed_by: reviewedBy,
      notes,
      cancelled,
      // P3: a note typed and then abandoned by unchecking "Cancelled" must
      // not persist onto a report marked not-cancelled — blanked here rather
      // than clearing `cancellationNote` itself, so re-checking the box
      // still has what was typed.
      cancellation_note: cancelled ? cancellationNote : '',
      // id: every loaded row carries one — see EquipmentRow's own comment
      // and this file's header. `days` is retired but still required on the
      // wire; sent computed, from the same rule that bills it, rather than
      // typed. start_date/end_date are ALWAYS sent, even blank — a blank one
      // is the real, explicit value "use the card's own range", not "leave
      // this column alone" (that reading is only for a legacy client that
      // never mentions the key at all).
      equipment: equipment.map((row) => ({
        id: row.id ?? undefined,
        equipment: row.equipment,
        days: rowDayCount(row.start_date, row.end_date) ?? 0,
        start_date: row.start_date,
        end_date: row.end_date,
        rate_unit: row.rate_unit
      })),
      operators: operators.map((row) => ({
        name: row.name,
        days: toNumber(row.days) ?? 0,
        time_in: row.time_in,
        time_out: row.time_out
      })),
      activities: activities.map((row) => ({ ...row })),
      // cost_per_day: sent only from an admin session — a quality session's
      // row never carries a typed figure to send (see HireRow's own
      // comment). The RAW trimmed text is sent, never toNumber(...)'d
      // first (Task 5 review, C1): toNumber returns null for anything it
      // cannot parse, and `?? undefined` then read that null as "not
      // supplied" — which PRESERVES the stored cost and reports success —
      // so a typo ("45O", a comma-formatted "1,200") was silently
      // discarded instead of flagged. `readHireCost`
      // (quality-contract-reports.ts) already accepts a string and returns
      // a fields-keyed 422 for one that will not parse; sending the raw
      // text lets THAT validator be the one that decides, matching every
      // other money figure this app already lets the server judge. Only a
      // genuinely blank/whitespace field becomes `undefined` — the real
      // "not supplied" case, which SaveContractReportRequest's own comment
      // says preserves the stored value rather than zeroing it.
      hires: hires.map((row) => ({
        id: row.id ?? undefined,
        name: row.name,
        kind: row.kind,
        start_date: row.start_date,
        end_date: row.end_date,
        cost_per_day: $isAdmin && row.cost_per_day.trim() !== '' ? row.cost_per_day.trim() : undefined
      }))
    };
  }

  function chooseSite(site: SiteSummary | null) {
    siteId = site?.id ?? null;
  }

  // Opened from a site page ("New contract report for Kits"): pre-select
  // that site. Only on a NEW form, only when still empty.
  $effect(() => {
    const fromSite = siteIdFromQuery($appLocation.query);
    if (contractReportId === null && fromSite !== null && siteId === null) {
      siteId = fromSite;
    }
  });

  let printing = $state(false);

  /**
   * Print what is on screen, not what was last saved: the author expects the
   * document to match the form in front of them. Ids and timestamps are
   * placeholders because the builder does not print them.
   */
  function print() {
    if (printing) return;
    printing = true;
    const stamp = new Date().toISOString();
    const started = printContractReport(
      {
        contract_report: {
          id: savedId ?? 0,
          site_id: siteId,
          report_date: reportDate,
          end_date: endDate,
          ticket_no: ticketNo,
          prepared_by: preparedBy,
          reviewed_by: reviewedBy,
          notes,
          cancelled: cancelled ? 1 : 0,
          cancellation_note: cancellationNote,
          created_at: stamp,
          updated_at: stamp
        },
        equipment: equipment.map((row, index) => ({
          id: index + 1,
          report_id: savedId ?? 0,
          seq: index + 1,
          equipment: row.equipment,
          // `days` is retired but the printed row still wants a NUMBER
          // (ContractReportEquipment) — computed the same way body() does,
          // never the typed quantity this row no longer has.
          days: rowDayCount(row.start_date, row.end_date) ?? 0,
          start_date: row.start_date,
          end_date: row.end_date,
          rate_unit: row.rate_unit
        })),
        // No `cost_per_day` here. The printed card takes
        // `PublicContractReportHire` (quality-pdf.ts), which does not carry
        // the field at all, and this object simply never reads it off the
        // form's own state — the printed card is a field record for either
        // session, not just a non-admin one.
        //
        // The TYPE does not enforce that, whatever it looks like: excess
        // property checking is lost through `.map`, so adding `cost_per_day`
        // back here compiles clean with 0 svelte-check errors (final review,
        // I3 — an earlier version of this comment claimed the compiler
        // stopped it, and it does not). What actually guards this is the
        // source tripwire in tests/quality.test.ts pinning the field list of
        // this exact object, plus quality-pdf's no-money test. Do not relax
        // either on the strength of the type.
        hires: hires.map((row, index) => ({
          id: index + 1,
          report_id: savedId ?? 0,
          seq: index + 1,
          name: row.name,
          kind: row.kind,
          start_date: row.start_date,
          end_date: row.end_date
        })),
        operators: operators.map((row, index) => ({
          id: index + 1,
          report_id: savedId ?? 0,
          seq: index + 1,
          name: row.name,
          days: toNumber(row.days) ?? 0,
          time_in: row.time_in,
          time_out: row.time_out
        })),
        activities: activities.map((row, index) => ({
          id: index + 1,
          report_id: savedId ?? 0,
          seq: index + 1,
          ...row,
          mix_design_id: row.mix_design_id ?? null
        })),
        exported_at: stamp
      },
      {
        onDone: (cause) => {
          printing = false;
          if (cause) {
            notify({
              tone: 'warn',
              title: 'Could not open the print view',
              detail: 'Try again, or use your browser’s own Print command.',
              sticky: true
            });
          }
        }
      }
    );

    if (!started) {
      printing = false;
      notify({
        tone: 'warn',
        title: 'This browser will not print',
        detail: 'Use your browser’s own Print command instead.',
        sticky: true
      });
    }
  }

  async function save(opts: { auto?: boolean } = {}) {
    if (saving || removed) return;
    saving = true;
    error = null;
    fieldErrors = {};
    try {
      const response =
        savedId === null
          ? await api.createContractReport(body())
          : await api.updateContractReport(savedId, body());

      const isNew = savedId === null;
      savedId = response.contract_report.id;
      // The server hands a new report its running ticket number.
      ticketNo = response.contract_report.ticket_no;
      // The baseline is what the form NOW holds (ticket number included), so
      // the server filling the ticket in does not read as a fresh edit.
      lastSaved = JSON.stringify(body());
      if (!opts.auto) {
        notify({ tone: 'ok', title: isNew ? 'Contract report created' : 'Contract report saved' });
      }
      // A new report gets its own address, so a refresh or a back button
      // lands on the saved sheet rather than a blank one. An AUTO save only
      // rewrites the address bar: `navigate` would change the route param and
      // the {#key} around this screen would tear the form down mid-keystroke.
      if (isNew) {
        if (opts.auto) claimAddress(routePath.qualityContractReport(savedId));
        else navigate(routePath.qualityContractReport(savedId), { replace: true, keepScroll: true });
      }
    } catch (cause) {
      if (isApiError(cause)) {
        error = cause.message;
        fieldErrors = cause.fields ?? {};
      } else {
        error = 'Could not save. Check the connection and try again.';
      }
    } finally {
      saving = false;
    }
  }

  /* --- auto-save (lib/autosave.ts; same wiring as DailyReportForm) -------- */

  /** JSON of the document at its last save/load — the dirty check's anchor. */
  let lastSaved = $state<string | null>(null);
  /** Set once the report is deleted, so no timer can quietly resurrect it. */
  let removed = false;

  /**
   * Does a NEW form hold something worth creating a report (and burning a
   * ticket number) for? The site prefill deliberately does not count — only
   * something a person typed, ticked, or a row they added.
   */
  function hasContent(): boolean {
    return (
      reportDate.trim() !== '' ||
      endDate.trim() !== '' ||
      notes.trim() !== '' ||
      preparedBy.trim() !== '' ||
      reviewedBy.trim() !== '' ||
      cancelled ||
      cancellationNote.trim() !== '' ||
      equipment.length > 0 ||
      operators.length > 0 ||
      activities.length > 0 ||
      hires.length > 0
    );
  }

  const dirty = $derived(lastSaved !== null && JSON.stringify(body()) !== lastSaved);

  const autosave = createAutosave({
    snapshot: () => JSON.stringify(body()),
    saved: () => lastSaved,
    busy: () => saving || deleting,
    blocked: () => removed,
    ready: () => savedId !== null || hasContent(),
    save: () => void save({ auto: true })
  });

  $effect(() => {
    const snapshot = JSON.stringify(body()); // subscribes to every field
    if (loading) return;
    if (snapshot === lastSaved) return;
    autosave.changed();
  });

  // A timer must not outlive the form (navigation away mid-edit).
  $effect(() => {
    return () => autosave.stop();
  });

  /**
   * Delete, behind an in-page confirmation step rather than a confirm() —
   * the app uses no modal dialogs anywhere.
   */
  let confirmingDelete = $state(false);

  async function remove() {
    if (savedId === null || deleting) return;
    deleting = true;
    try {
      await api.deleteContractReport(savedId);
      // No auto-save timer may quietly resurrect what was just deleted.
      removed = true;
      autosave.stop();
      notify({ tone: 'ok', title: 'Contract report deleted' });
      navigate(routePath.qualityContractReportList(siteId ?? undefined), { replace: true });
    } catch (cause) {
      notifyError(cause, 'Could not delete that contract report');
    } finally {
      deleting = false;
      confirmingDelete = false;
    }
  }
</script>

<!-- Enter moves to the next field; see lib/fields/advance-on-enter.ts. -->
<div use:advanceOnEnter>
{#if loading}
  <p class="meta center"><span class="spinner" aria-hidden="true"></span> Loading…</p>
{:else}
  {#if error}
    <div class="banner banner--error" role="alert">
      <div><strong>Could not save</strong>{error}</div>
    </div>
  {/if}

  <div class="card">
    <div class="card__body">
      <div class="titlebar">
        <h1 class="card__title">{savedId === null ? 'New contract report' : ticketNo ? `Ticket ${ticketNo}` : 'Contract report'}</h1>
        <a class="btn btn--sm" href={routePath.qualityContractReportList(siteId ?? undefined)} onclick={linkHandler}>
          All contract reports
        </a>
      </div>

      <SitePicker value={siteId} onchoose={chooseSite} />

      <div class="grid2">
        <label class="field">
          <span class="label">Date</span>
          <input type="date" bind:value={reportDate} />
          {#if fieldErrors.report_date}<span class="field__flag">{fieldErrors.report_date}</span>{/if}
        </label>
        <label class="field">
          <span class="label">End date</span>
          <input type="date" bind:value={endDate} />
          <span class="field__hint">Blank means the card is one day.</span>
          {#if fieldErrors.end_date}<span class="field__flag">{fieldErrors.end_date}</span>{/if}
        </label>
      </div>
      <label class="field">
        <span class="label">Ticket #</span>
        <input type="text" bind:value={ticketNo} readonly={savedId === null} placeholder={savedId === null ? 'Next number, on save' : ''} />
      </label>
    </div>
  </div>

  <div class="card">
    <div class="card__body">
      <label class="field field--check">
        <input type="checkbox" bind:checked={cancelled} />
        <span class="label">Cancelled</span>
      </label>
      {#if cancelled}
        <label class="field">
          <span class="label">Cancellation note</span>
          <textarea rows="2" bind:value={cancellationNote} placeholder="Why the day was cancelled"></textarea>
        </label>
      {/if}
    </div>
  </div>

  <div class="card">
    <div class="card__body">
      <h2 class="card__title">Equipment</h2>
      <p class="meta">Leave a row's Start/End blank to bill it over the card's own range above.</p>

      <div class="rows rows--equipment">
        <div class="rows__head" aria-hidden="true">
          <span>Equipment</span>
          <span>Start</span>
          <span>End</span>
          <span>Days</span>
          <span></span>
        </div>

        {#each equipment as row, index (index)}
          <div class="row">
            <div class="row__fields">
              <label class="row__field">
                <span class="label">Equipment</span>
                <!--
                  Picked, not typed, so the admin area's rate match cannot be
                  defeated by a spelling. "Something else…" is the escape
                  hatch: a machine nobody has set up yet still goes on the
                  sheet, and shows up in Rates needing a $/day.
                -->
                <select
                  value={equipmentValue(index)}
                  onchange={(event) => chooseEquipment(index, (event.currentTarget as HTMLSelectElement).value)}
                  aria-label="Equipment {index + 1} name"
                >
                  <option value="">—</option>
                  {#each equipmentChoices as name, choice (name)}
                    <option value={String(choice)}>{name}</option>
                  {/each}
                  <option value={OTHER_EQUIPMENT}>Something else…</option>
                </select>
                {#if customEquipment[index]}
                  <input
                    type="text"
                    bind:value={row.equipment}
                    placeholder="Equipment name"
                    aria-label="Equipment {index + 1} typed name"
                  />
                {/if}
              </label>
              <label class="row__field">
                <span class="label">Start</span>
                <input type="date" bind:value={row.start_date} aria-label="Equipment {index + 1} start date" />
              </label>
              <label class="row__field">
                <span class="label">End</span>
                <input type="date" bind:value={row.end_date} aria-label="Equipment {index + 1} end date" />
              </label>
              <!--
                Which of the machine's rates this row bills on. The days still
                come from the dates above; this only picks day vs week vs month.
                This screen never sees the rates themselves — only the unit.
              -->
              <label class="row__field">
                <span class="label">Bill by</span>
                <select bind:value={row.rate_unit} aria-label="Equipment {index + 1} billing unit">
                  <option value="day">Day</option>
                  <option value="week">Week</option>
                  <option value="month">Month</option>
                </select>
              </label>
              <!-- div, not label: a read-only figure has no control to
                   associate a label with; `.row__field .label` styling does
                   not care which tag it hangs on. -->
              <div class="row__field">
                <span class="label">Days</span>
                <span class="readonly-value">{rowDayCount(row.start_date, row.end_date) ?? '—'}</span>
              </div>
            </div>

            <div class="row__actions">
              <button
                class="btn btn--sm btn--ghost"
                type="button"
                onclick={() => moveEquipment(index, -1)}
                disabled={index === 0}
                aria-label="Move {row.equipment || `equipment ${index + 1}`} up"
              >↑</button>
              <button
                class="btn btn--sm btn--ghost"
                type="button"
                onclick={() => moveEquipment(index, 1)}
                disabled={index === equipment.length - 1}
                aria-label="Move {row.equipment || `equipment ${index + 1}`} down"
              >↓</button>
              <button
                class="btn btn--sm btn--ghost"
                type="button"
                onclick={() => removeEquipment(index)}
                aria-label="Remove {row.equipment || `equipment ${index + 1}`}"
              >✕</button>
            </div>
          </div>
        {/each}
      </div>

      <!--
        List-level: the id-stable-reconcile guard (quality-contract-reports.ts's
        checkEquipmentDatesSurvive) refuses a save that would silently drop a
        dated row it cannot account for — keyed to `equipment`, not to one row,
        since it is about the SET, not a single field.
      -->
      {#if fieldErrors.equipment}<p class="field__flag">{fieldErrors.equipment}</p>{/if}

      <button class="btn btn--block" type="button" onclick={addEquipment}>Add equipment</button>
    </div>
  </div>

  <div class="card">
    <div class="card__body">
      <h2 class="card__title">Hired in</h2>
      <p class="meta">
        A rented machine or a subcontractor's crew SCI brought onto the job — separate from SCI's own equipment above.
        Leave a row's Start/End blank to bill it over the card's own range above.
      </p>

      <div class="rows rows--hire" class:priced={$isAdmin}>
        <div class="rows__head" aria-hidden="true">
          <span>Name</span>
          <span>Kind</span>
          <span>Start</span>
          <span>End</span>
          <span>Days</span>
          {#if $isAdmin}<span>$ / day</span>{/if}
          <span></span>
        </div>

        {#each hires as row, index (index)}
          <div class="row">
            <div class="row__fields">
              <label class="row__field">
                <span class="label">Name</span>
                <input type="text" bind:value={row.name} placeholder="Name" aria-label="Hire {index + 1} name" />
              </label>
              <label class="row__field">
                <span class="label">Kind</span>
                <select bind:value={row.kind} aria-label="Hire {index + 1} kind">
                  <option value="equipment">Equipment</option>
                  <option value="subcontractor">Subcontractor</option>
                </select>
              </label>
              <label class="row__field">
                <span class="label">Start</span>
                <input type="date" bind:value={row.start_date} aria-label="Hire {index + 1} start date" />
              </label>
              <label class="row__field">
                <span class="label">End</span>
                <input type="date" bind:value={row.end_date} aria-label="Hire {index + 1} end date" />
              </label>
              <!-- div, not label: a read-only figure has no control to
                   associate a label with; `.row__field .label` styling does
                   not care which tag it hangs on. -->
              <div class="row__field">
                <span class="label">Days</span>
                <span class="readonly-value">{rowDayCount(row.start_date, row.end_date) ?? '—'}</span>
              </div>
              {#if $isAdmin}
                <!--
                  Admin only, no read-only fallback for a quality session —
                  UNLIKE bills_concrete's precedent in SiteFields.svelte. See
                  this file's header comment: cost_per_day is money, and no
                  money reaches a quality screen, so the input is simply
                  absent rather than shown disabled.
                -->
                <label class="row__field">
                  <span class="label">$ / day</span>
                  <input
                    type="text"
                    inputmode="decimal"
                    bind:value={row.cost_per_day}
                    placeholder="0.00"
                    aria-label="Hire {index + 1} cost per day"
                  />
                  <!--
                    readHireCost (quality-contract-reports.ts) keys an
                    unparsable figure to `hires.<index>.cost_per_day` —
                    body() now sends this field's raw text rather than
                    silently discarding a typo client-side (Task 5 review,
                    C1), so THIS is where that 422 lands.
                  -->
                  {#if fieldErrors[`hires.${index}.cost_per_day`]}
                    <span class="field__flag">{fieldErrors[`hires.${index}.cost_per_day`]}</span>
                  {/if}
                </label>
              {/if}
            </div>

            <div class="row__actions">
              <button
                class="btn btn--sm btn--ghost"
                type="button"
                onclick={() => moveHire(index, -1)}
                disabled={index === 0}
                aria-label="Move {row.name || `hire ${index + 1}`} up"
              >↑</button>
              <button
                class="btn btn--sm btn--ghost"
                type="button"
                onclick={() => moveHire(index, 1)}
                disabled={index === hires.length - 1}
                aria-label="Move {row.name || `hire ${index + 1}`} down"
              >↓</button>
              <button
                class="btn btn--sm btn--ghost"
                type="button"
                onclick={() => removeHire(index)}
                aria-label="Remove {row.name || `hire ${index + 1}`}"
              >✕</button>
            </div>
          </div>
        {/each}
      </div>

      <!-- List-level: checkHireCostsSurvive's own guard, keyed to `hires`. -->
      {#if fieldErrors.hires}<p class="field__flag">{fieldErrors.hires}</p>{/if}

      <button class="btn btn--block" type="button" onclick={addHire}>Add hire</button>
    </div>
  </div>

  {#if cancelled}
    <div class="card">
      <div class="card__body">
        <h2 class="card__title">Operators</h2>
        <!--
          Not offered at all while Cancelled is ticked, rather than shown
          empty or disabled — calc/contract.ts's own rule: the crew was stood
          down, so no operator days bill; billing them on top of the
          cancellation fee would charge twice for the same non-work.
          Equipment and hires above still bill — the machine was on site
          either way.
        -->
        <p class="meta">
          No operator days bill on a cancelled day — the crew was stood down, and the cancellation fee covers it instead.
        </p>
      </div>
    </div>
  {:else}
  <div class="card">
    <div class="card__body">
      <h2 class="card__title">Operators</h2>

      <div class="rows rows--operator">
        <div class="rows__head" aria-hidden="true">
          <span>Name</span>
          <span>Days</span>
          <span>Time in</span>
          <span>Time out</span>
          <span></span>
        </div>

        {#each operators as row, index (index)}
          <div class="row">
            <div class="row__fields">
              <label class="row__field">
                <span class="label">Name</span>
                <select
                  value={operatorValue(index)}
                  onchange={(event) => chooseOperator(index, (event.currentTarget as HTMLSelectElement).value)}
                  aria-label="Operator {index + 1} name"
                >
                  <option value="">—</option>
                  {#each operatorChoices as name, choice (name)}
                    <option value={String(choice)}>{name}</option>
                  {/each}
                  <option value={OTHER_OPERATOR}>Someone else…</option>
                </select>
                {#if customOperator[index]}
                  <input
                    type="text"
                    bind:value={row.name}
                    placeholder="Name"
                    aria-label="Operator {index + 1} typed name"
                  />
                {/if}
              </label>
              <label class="row__field">
                <span class="label">Days</span>
                <input
                  type="text"
                  inputmode="decimal"
                  bind:value={row.days}
                  placeholder="1"
                  aria-label="Operator {index + 1} days"
                />
              </label>
              <label class="row__field">
                <span class="label">Time in</span>
                <input
                  type="text"
                  bind:value={row.time_in}
                  onblur={() => (row.time_in = quickTime(row.time_in))}
                  placeholder="7:00 AM"
                  aria-label="Operator {index + 1} time in"
                />
              </label>
              <label class="row__field">
                <span class="label">Time out</span>
                <input
                  type="text"
                  bind:value={row.time_out}
                  onblur={() => (row.time_out = quickTime(row.time_out))}
                  placeholder="3:30 PM"
                  aria-label="Operator {index + 1} time out"
                />
              </label>
            </div>

            <div class="row__actions">
              <button
                class="btn btn--sm btn--ghost"
                type="button"
                onclick={() => moveOperator(index, -1)}
                disabled={index === 0}
                aria-label="Move {row.name || `operator ${index + 1}`} up"
              >↑</button>
              <button
                class="btn btn--sm btn--ghost"
                type="button"
                onclick={() => moveOperator(index, 1)}
                disabled={index === operators.length - 1}
                aria-label="Move {row.name || `operator ${index + 1}`} down"
              >↓</button>
              <button
                class="btn btn--sm btn--ghost"
                type="button"
                onclick={() => removeOperator(index)}
                aria-label="Remove {row.name || `operator ${index + 1}`}"
              >✕</button>
            </div>
          </div>
        {/each}
      </div>

      <button class="btn btn--block" type="button" onclick={addOperator}>Add operator</button>
    </div>
  </div>
  {/if}

  <div class="card">
    <div class="card__body">
      <h2 class="card__title">Activities</h2>

      <div class="rows rows--activity">
        <div class="rows__head" aria-hidden="true">
          <span>Piling #</span>
          <span>Unit #</span>
          <span>Start</span>
          <span>Stop</span>
          <span>Amount supplied</span>
          <span>Admixtures used</span>
          <span>Mix design</span>
          <span></span>
        </div>

        {#each activities as activity, index (index)}
          <div class="row">
            <div class="row__fields">
              <label class="row__field">
                <span class="label">Piling #</span>
                <input
                  type="text"
                  bind:value={activity.piling_number}
                  aria-label="Activity {index + 1} piling number"
                />
              </label>
              <label class="row__field">
                <span class="label">Unit #</span>
                <input
                  type="text"
                  bind:value={activity.unit_number}
                  aria-label="Activity {index + 1} unit number"
                />
              </label>
              <label class="row__field">
                <span class="label">Start</span>
                <input
                  type="text"
                  bind:value={activity.start_time}
                  onblur={() => (activity.start_time = quickTime(activity.start_time))}
                  placeholder="7:00 AM"
                  aria-label="Activity {index + 1} start time"
                />
              </label>
              <label class="row__field">
                <span class="label">Stop</span>
                <input
                  type="text"
                  bind:value={activity.stop_time}
                  onblur={() => (activity.stop_time = quickTime(activity.stop_time))}
                  placeholder="3:30 PM"
                  aria-label="Activity {index + 1} stop time"
                />
              </label>
              <label class="row__field">
                <span class="label">Amount supplied</span>
                <input
                  type="text"
                  inputmode="decimal"
                  bind:value={activity.amount_supplied}
                  onblur={() => (activity.amount_supplied = quickCubes(activity.amount_supplied))}
                  aria-label="Activity {index + 1} amount supplied"
                />
              </label>
              <label class="row__field">
                <span class="label">Admixtures used</span>
                <input
                  type="text"
                  bind:value={activity.admixtures_used}
                  aria-label="Activity {index + 1} admixtures used"
                />
              </label>
              <label class="row__field">
                <span class="label">Mix design</span>
                <select
                  value={activity.mix_design_id === null ? '' : String(activity.mix_design_id)}
                  onchange={(event) => chooseMix(index, (event.currentTarget as HTMLSelectElement).value)}
                  aria-label="Activity {index + 1} mix design"
                >
                  <option value="">{activity.water_used && activity.mix_design_id === null ? `(${activity.water_used})` : '—'}</option>
                  {#each mixOptions as option (option.id)}
                    <option value={String(option.id)}>{option.mix_code}{option.project_name ? ` — ${option.project_name}` : ''}</option>
                  {/each}
                </select>
              </label>
            </div>

            <div class="row__actions">
              <button
                class="btn btn--sm btn--ghost"
                type="button"
                onclick={() => moveActivity(index, -1)}
                disabled={index === 0}
                aria-label="Move activity {index + 1} up"
              >↑</button>
              <button
                class="btn btn--sm btn--ghost"
                type="button"
                onclick={() => moveActivity(index, 1)}
                disabled={index === activities.length - 1}
                aria-label="Move activity {index + 1} down"
              >↓</button>
              <button
                class="btn btn--sm btn--ghost"
                type="button"
                onclick={() => removeActivity(index)}
                aria-label="Remove activity {index + 1}"
              >✕</button>
            </div>
          </div>
        {/each}

        {#if activities.length === 0}
          <p class="meta">No activity rows yet.</p>
        {:else}
          <!--
            The day's concrete, under the Amount supplied column — the same
            `totalCubes` the printed sheet and the admin pours table use, so
            the crew, the PDF and the office all read one number. A row that
            does not read as m³ is left out, and a dash shows when none do.
          -->
          <div class="rows__foot" aria-live="polite">
            <span class="rows__foot-label">Total</span>
            <span class="rows__foot-value">{cubes(totalCubes(activities.map((row) => row.amount_supplied)))}</span>
          </div>
        {/if}
      </div>

      <button class="btn btn--block" type="button" onclick={addActivity}>Add activity</button>
    </div>
  </div>

  <div class="card">
    <div class="card__body">
      <label class="field">
        <span class="label">Notes</span>
        <textarea rows="5" bind:value={notes}></textarea>
      </label>

      <div class="grid2">
        <label class="field">
          <span class="label">Prepared by</span>
          <input type="text" bind:value={preparedBy} />
        </label>
        <label class="field">
          <span class="label">Reviewed by</span>
          <input type="text" bind:value={reviewedBy} />
        </label>
      </div>
    </div>
  </div>

  <div class="card">
    <div class="card__body">
      <button class="btn btn--block" type="button" disabled={printing} onclick={print}>
        {#if printing}<span class="spinner" aria-hidden="true"></span> Preparing…{:else}Print / Save as PDF{/if}
      </button>

      <button class="btn btn--primary btn--block btn--big" type="button" disabled={saving} onclick={() => save()}>
        {#if saving}<span class="spinner" aria-hidden="true"></span> Saving…{:else}Save contract report{/if}
      </button>

      <!-- The auto-save's word on where things stand. Only ever informational:
           the button above still saves on demand. -->
      <p class="autosave" role="status">
        {#if saving}
          Saving…
        {:else if savedId !== null && !dirty}
          All changes saved.
        {:else if savedId !== null || hasContent()}
          Unsaved changes — saving automatically…
        {:else}
          Saves automatically as you fill it in.
        {/if}
      </p>

      {#if $isAdmin && savedId !== null}
        {#if confirmingDelete}
          <div class="banner banner--warn" role="status">
            <div class="spacer">
              <strong>Delete this contract report?</strong>
              This cannot be undone.
              <div class="banner__actions">
                <button class="btn btn--sm btn--danger" type="button" disabled={deleting} onclick={remove}>
                  {#if deleting}Deleting…{:else}Delete{/if}
                </button>
                <button class="btn btn--sm" type="button" onclick={() => (confirmingDelete = false)}>
                  Keep it
                </button>
              </div>
            </div>
          </div>
        {:else}
          <button class="btn btn--block" type="button" onclick={() => (confirmingDelete = true)}>
            Delete contract report
          </button>
        {/if}
      {/if}
    </div>
  </div>
{/if}
</div>

<style>
  /* Informational, never alarming: where the auto-save stands. */
  .autosave {
    margin: 0.4rem 0 0;
    font-size: 0.78rem;
    color: var(--ink-faint);
    text-align: center;
  }

  .titlebar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.6rem;
    margin-bottom: 0.6rem;
  }

  .titlebar .card__title {
    margin: 0;
  }

  .field--check {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
  }

  .field--check input {
    flex: 0 0 auto;
    width: 1.2rem;
    height: 1.2rem;
  }

  /*
    Shared structure for all four tables (equipment, hires, operators,
    activities), copied directly from DailyReportForm.svelte's own tables —
    see that file's comment for the full reasoning and the font-size-at-
    breakpoint measurement behind these floors. Below 60rem every field in a
    row stacks into its own labelled block; at and above it, one line per
    row under a shared --rowcols column template, with .row__fields dropping
    out of layout via display:contents so its labelled children join the
    row's own grid directly as columns 1..N.
  */
  .rows--equipment {
    --rowcols: minmax(9rem, 1fr) minmax(7.5rem, 1fr) minmax(7.5rem, 1fr) 4rem calc(3 * var(--tap) + 0.6rem);
  }

  /*
    Two widths: `cost_per_day` is admin-only (see this file's header
    comment), so a quality session's grid has one fewer column than an
    admin session's — `.priced` is added by the template only when
    `$isAdmin`, never both at once for the same viewer.
  */
  .rows--hire {
    --rowcols: minmax(8rem, 1fr) 7rem minmax(7.5rem, 1fr) minmax(7.5rem, 1fr) 4rem calc(3 * var(--tap) + 0.6rem);
  }

  .rows--hire.priced {
    --rowcols: minmax(8rem, 1fr) 7rem minmax(7.5rem, 1fr) minmax(7.5rem, 1fr) 4rem 6rem calc(3 * var(--tap) + 0.6rem);
  }

  .rows--operator {
    --rowcols: minmax(8rem, 1fr) 5.5rem 7rem 7rem calc(3 * var(--tap) + 0.6rem);
  }

  .rows--activity {
    --rowcols:
      minmax(5rem, 1fr) minmax(5rem, 1fr) minmax(4rem, 1fr) minmax(4rem, 1fr)
      minmax(5.5rem, 1fr) minmax(5.5rem, 1fr) minmax(4.5rem, 1fr) calc(3 * var(--tap) + 0.6rem);
  }

  .row__fields {
    display: grid;
    gap: 0.6rem;
  }

  .row__field {
    display: block;
    min-width: 0;
  }

  /* The free-text box, revealed under the pick-list it came from. */
  .row__field select + input {
    margin-top: 0.4rem;
  }

  .rows__head {
    display: none;
  }

  /*
    The total under Activities. Stacked on a phone it is one line, label then
    number; on the grid it takes the row's own columns so the number lands
    under Amount supplied, the fifth column, with the label filling the four
    before it.
  */
  .rows__foot {
    display: flex;
    justify-content: space-between;
    gap: 0.6rem;
    padding: 0.6rem 0 0;
    border-top: 2px solid var(--line);
    font-weight: 700;
  }

  .rows__foot-value {
    font-variant-numeric: tabular-nums;
  }

  .rows__foot-label {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--ink-soft);
    align-self: center;
  }

  .row {
    border-bottom: 1px solid var(--line-soft);
    padding: 0.85rem 0;
  }

  .row:first-child {
    padding-top: 0;
  }

  .row:last-of-type {
    border-bottom: 0;
  }

  .row__actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.3rem;
    margin-top: 0.6rem;
  }

  @media (min-width: 60rem) {
    .rows__head {
      display: grid;
      grid-template-columns: var(--rowcols);
      column-gap: 0.6rem;
      padding: 0 0 0.4rem;
      border-bottom: 2px solid var(--line);
    }

    .rows__head span {
      font-size: 0.75rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      color: var(--ink-soft);
    }

    .rows__foot {
      display: grid;
      grid-template-columns: var(--rowcols);
      column-gap: 0.6rem;
      align-items: center;
    }

    .rows__foot-label {
      grid-column: 1 / 5;
    }

    .rows__foot-value {
      grid-column: 5;
    }

    .row {
      display: grid;
      grid-template-columns: var(--rowcols);
      column-gap: 0.6rem;
      align-items: center;
      padding: 0.4rem 0;
    }

    .row__fields {
      display: contents;
    }

    .row__field .label {
      display: none;
    }

    .row__actions {
      justify-content: flex-start;
      margin-top: 0;
    }
  }
</style>
